import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  MarkStampBillableChargeInvoicedPayload,
  StampAmountMapSavePayload,
  StampGroupLineSavePayload,
  StampGroupMemberResetPayload,
  StampGroupMemberSavePayload,
  StampGroupSavePayload,
  StampGroupLinesBulkSavePayload,
  UpdateStampBillableChargePayload,
} from '../../../models/parking/stamp-discount.model';

@Injectable({ providedIn: 'root' })
export class ApiStampDiscountService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);
  private readonly http = inject(HttpClient);

  private get token(): string {
    return this.cookieService.get('token');
  }

  getStampGroupForTable(params: {
    page?: number;
    pageSize?: number;
    searchText?: string;
    siteId?: number | null;
    active?: boolean | null;
  }): Observable<unknown> {
    const query: Record<string, string | number | boolean> = {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.searchText) query['searchText'] = params.searchText;
    if (params.siteId) query['siteId'] = params.siteId;
    if (params.active != null) query['active'] = params.active;
    return this.apiMethodService.request('/StampDiscount/getStampGroupForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampGroupForTable',
      token: this.token,
      headerOnly: false,
      params: query,
    });
  }

  getStampGroupById(id: number): Observable<unknown> {
    return this.apiMethodService.request('/StampDiscount/getStampGroupById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampGroupById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  getStampGroupLogs(groupId: number, page = 1, pageSize = 20): Observable<unknown> {
    return this.apiMethodService.request('/StampDiscount/getStampGroupLogs', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampGroupLogs',
      token: this.token,
      headerOnly: false,
      params: { groupId, page, pageSize },
    });
  }

  getStampDiscountRateOptions(siteId?: number | null): Observable<unknown> {
    return this.apiMethodService.request('/StampDiscount/getStampDiscountRateOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampDiscountRateOptions',
      token: this.token,
      headerOnly: false,
      params: siteId ? { siteId } : {},
    });
  }

  getStampCustomerOptions(searchText?: string): Observable<unknown> {
    return this.apiMethodService.request('/StampDiscount/getStampCustomerOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampCustomerOptions',
      token: this.token,
      headerOnly: false,
      params: searchText ? { searchText } : {},
    });
  }

  getStampUserOptions(params?: {
    page?: number;
    pageSize?: number;
    searchText?: string;
    excludeGroupId?: number | null;
  }): Observable<unknown> {
    const query: Record<string, string | number> = {
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 20,
    };
    if (params?.searchText) query['searchText'] = params.searchText;
    if (params?.excludeGroupId) query['excludeGroupId'] = params.excludeGroupId;
    return this.apiMethodService.request('/StampDiscount/getStampUserOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampUserOptions',
      token: this.token,
      headerOnly: false,
      params: query,
    });
  }

  getStampAmountMapForTable(params: {
    siteId: number;
    page?: number;
    pageSize?: number;
    searchText?: string;
    active?: boolean | null;
  }): Observable<unknown> {
    const query: Record<string, string | number | boolean> = {
      siteId: params.siteId,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.searchText) query['searchText'] = params.searchText;
    if (params.active != null) query['active'] = params.active;
    return this.apiMethodService.request('/StampDiscount/getStampAmountMapForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampAmountMapForTable',
      token: this.token,
      headerOnly: false,
      params: query,
    });
  }

  getStampAmountMapById(id: number): Observable<unknown> {
    return this.apiMethodService.request('/StampDiscount/getStampAmountMapById', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampAmountMapById',
      token: this.token,
      headerOnly: false,
      params: { id },
    });
  }

  getStampMembersForTable(params: {
    page?: number;
    pageSize?: number;
    searchText?: string;
    siteId?: number | null;
  }): Observable<unknown> {
    const query: Record<string, string | number> = {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.searchText) query['searchText'] = params.searchText;
    if (params.siteId) query['siteId'] = params.siteId;
    return this.apiMethodService.request('/StampDiscount/getStampMembersForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampMembersForTable',
      token: this.token,
      headerOnly: false,
      params: query,
    });
  }

  getStampBillableChargeForTable(params: {
    page?: number;
    pageSize?: number;
    startDate?: string;
    endDate?: string;
    status?: string | null;
    cpCustomerId?: number | null;
    siteId?: number | null;
    searchText?: string;
  }): Observable<unknown> {
    const query: Record<string, string | number> = {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.startDate) query['startDate'] = params.startDate;
    if (params.endDate) query['endDate'] = params.endDate;
    if (params.status) query['status'] = params.status;
    if (params.cpCustomerId) query['cpCustomerId'] = params.cpCustomerId;
    if (params.siteId) query['siteId'] = params.siteId;
    if (params.searchText) query['searchText'] = params.searchText;
    return this.apiMethodService.request('/StampDiscount/getStampBillableChargeForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampBillableChargeForTable',
      token: this.token,
      headerOnly: false,
      params: query,
    });
  }

  getStampBillableCustomerOptions(siteId: number, searchText?: string): Observable<unknown> {
    const params: Record<string, string | number> = { siteId };
    if (searchText) params['searchText'] = searchText;
    return this.apiMethodService.request('/StampDiscount/getStampBillableCustomerOptions', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getStampBillableCustomerOptions',
      token: this.token,
      headerOnly: false,
      params,
    });
  }

  getParkingWebStampForTable(params: {
    siteId: number;
    startDate: string;
    endDate: string;
    page?: number;
    pageSize?: number;
    searchField?: string;
    searchText?: string;
  }): Observable<unknown> {
    const query: Record<string, string | number> = {
      siteId: params.siteId,
      startDate: params.startDate,
      endDate: params.endDate,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.searchField) query['searchField'] = params.searchField;
    if (params.searchText) query['searchText'] = params.searchText;
    return this.apiMethodService.request('/StampDiscount/getParkingWebStampForTable', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'getParkingWebStampForTable',
      token: this.token,
      headerOnly: false,
      params: query,
    });
  }

  markStampBillableChargeInvoiced(body: MarkStampBillableChargeInvoicedPayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/StampDiscount/markStampBillableChargeInvoiced', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'markStampBillableChargeInvoiced',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  updateStampBillableCharge(body: UpdateStampBillableChargePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/StampDiscount/updateStampBillableCharge', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'updateStampBillableCharge',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  saveStampGroup(body: StampGroupSavePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/StampDiscount/saveStampGroup', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'saveStampGroup',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  saveStampGroupLine(body: StampGroupLineSavePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/StampDiscount/saveStampGroupLine', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'saveStampGroupLine',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  saveStampGroupLinesBulk(body: StampGroupLinesBulkSavePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/StampDiscount/saveStampGroupLinesBulk', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'saveStampGroupLinesBulk',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  saveStampGroupMember(body: StampGroupMemberSavePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/StampDiscount/saveStampGroupMember', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'saveStampGroupMember',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  resetStampGroupMemberQuota(body: StampGroupMemberResetPayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/StampDiscount/resetStampGroupMemberQuota', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'resetStampGroupMemberQuota',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  saveStampAmountMap(body: StampAmountMapSavePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request('/StampDiscount/saveStampAmountMap', 'POST', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'saveStampAmountMap',
      token: this.token,
      headerOnly: false,
      body,
    });
  }

  getStampBillableChargePdf(params: {
    siteId: number;
    cpCustomerId: number;
    startDate: string;
    endDate: string;
    status?: string | null;
    searchText?: string;
  }): Observable<Uint8Array> {
    let httpParams = new HttpParams()
      .set('siteId', params.siteId)
      .set('cpCustomerId', params.cpCustomerId)
      .set('startDate', params.startDate)
      .set('endDate', params.endDate);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.searchText) httpParams = httpParams.set('searchText', params.searchText);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.token}`,
      Accept: 'application/octet-stream, application/pdf, */*',
    });

    return this.http
      .get(`${this.baseUrl}/Reports/stamp-billable-charge/pdf`, {
        headers,
        params: httpParams,
        responseType: 'arraybuffer',
      })
      .pipe(map((buffer) => new Uint8Array(buffer)));
  }

  /** คำนวณค่าจอด local จาก transaction — ใช้ companyFee สำหรับรายการเรียกเก็บ */
  calculateLocalParkingFee(transactionId: number, siteId: number): Observable<unknown> {
    return this.apiMethodService.request('/ParkingFeeCalculation/calculate', 'GET', this.baseUrl, {
      isConsoleLog: true,
      labelLog: 'calculateLocalParkingFee',
      token: this.token,
      headerOnly: false,
      params: { transactionId, siteId },
    });
  }
}
